import React, { useState, useEffect } from 'react'
import Swal from 'sweetalert2'
import apiClient from '../../config/api'

const ManageUnits = () => {
  const [units, setUnits] = useState([])
  const [filteredUnits, setFilteredUnits] = useState([])
  const [loading, setLoading] = useState(true)
  const [filters, setFilters] = useState({
    search: ''
  })

  const fetchUnits = async () => {
    try {
      setLoading(true)
      const response = await apiClient.get('/api/products/units/')
      setUnits(response.data)
      setFilteredUnits(response.data)
    } catch (error) {
      console.error('Error fetching units:', error)
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.detail || 'Failed to fetch units. Please try again.'
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchUnits()
  }, [])

  useEffect(() => {
    let filtered = [...units]

    if (filters.search) {
      const searchLower = filters.search.toLowerCase()
      filtered = filtered.filter(unit =>
        unit.name?.toLowerCase().includes(searchLower) ||
        unit.abbreviation?.toLowerCase().includes(searchLower)
      )
    }

    setFilteredUnits(filtered)
  }, [units, filters])

  const handleEdit = async (unit) => {
    const escapedName = unit.name.replace(/"/g, '&quot;').replace(/'/g, '&#39;')
    const escapedAbbreviation = (unit.abbreviation || '').replace(/"/g, '&quot;').replace(/'/g, '&#39;')
    
    const { value: formValues } = await Swal.fire({
      title: 'Edit Unit',
      html: `
        <div style="text-align: left; padding: 10px 0;">
          <label style="display: block; margin-top: 15px; margin-bottom: 5px; font-weight: 600; color: #555; font-size: 14px;">Unit Name <span style="color: red;">*</span></label>
          <input id="swal-name" class="swal2-input" placeholder="Unit Name" value="${escapedName}" required style="margin-top: 0;">
          
          <label style="display: block; margin-top: 15px; margin-bottom: 5px; font-weight: 600; color: #555; font-size: 14px;">Abbreviation</label>
          <input id="swal-abbreviation" class="swal2-input" placeholder="Abbreviation (Optional)" value="${escapedAbbreviation}" style="margin-top: 0;">
        </div>
      `,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: 'Update',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#6c757d',
      width: '500px',
      preConfirm: () => {
        const name = document.getElementById('swal-name').value.trim()
        const abbreviation = document.getElementById('swal-abbreviation').value.trim()
        
        if (!name) {
          Swal.showValidationMessage('Unit name is required')
          return false
        }
        return { 
          name,
          abbreviation: abbreviation || null
        }
      },
      didOpen: () => {
        const input = document.getElementById('swal-name')
        input.focus()
        input.select()
      }
    })

    if (formValues) {
      try {
        await apiClient.put(`/api/products/units/${unit.id}/`, formValues)

        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'Unit updated successfully!'
        })

        fetchUnits()
      } catch (error) {
        console.error('Error updating unit:', error)
        const errorMessage = error.response?.data?.name?.[0] || 
                            error.response?.data?.abbreviation?.[0] ||
                            error.response?.data?.non_field_errors?.[0] ||
                            error.response?.data?.detail ||
                            error.response?.data?.message ||
                            'Failed to update unit. Please try again.'
        
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: errorMessage
        })
      }
    }
  }

  const handleDelete = async (unitId) => {
    const unit = units.find(u => u.id === unitId)
    
    const escapedName = unit?.name?.replace(/"/g, '&quot;').replace(/'/g, '&#39;') || ''
    
    const { value: typedName } = await Swal.fire({
      title: 'Delete Unit',
      html: `
        <p>Are you sure you want to delete this unit?</p>
        <p class="text-danger"><strong>This action cannot be undone!</strong></p>
        <p>Type <strong>"${escapedName}"</strong> to confirm:</p>
        <input id="swal-input" class="swal2-input" placeholder="Type unit name">
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
      allowOutsideClick: false,
      allowEscapeKey: true,
      didOpen: () => {
        const input = document.getElementById('swal-input')
        const confirmButton = Swal.getConfirmButton()
        
        confirmButton.disabled = true
        confirmButton.style.opacity = '0.5'
        confirmButton.style.cursor = 'not-allowed'
        
        input.addEventListener('input', () => {
          const inputValue = input.value.trim()
          const matches = inputValue === unit?.name
          
          if (matches) {
            confirmButton.disabled = false
            confirmButton.style.opacity = '1'
            confirmButton.style.cursor = 'pointer'
          } else {
            confirmButton.disabled = true
            confirmButton.style.opacity = '0.5'
            confirmButton.style.cursor = 'not-allowed'
          }
        })
        
        input.focus()
      },
      preConfirm: () => {
        const input = document.getElementById('swal-input')
        const inputValue = input.value.trim()
        
        if (!inputValue) {
          Swal.showValidationMessage('You need to type the unit name!')
          return false
        }
        
        if (inputValue !== unit?.name) {
          Swal.showValidationMessage('The name does not match!')
          return false
        }
        
        return inputValue
      }
    })

    if (typedName === unit?.name) {
      try {
        await apiClient.delete(`/api/products/units/${unitId}/`)
        Swal.fire({
          icon: 'success',
          title: 'Deleted!',
          text: 'Unit has been deleted.',
          timer: 2000,
          showConfirmButton: false
        })
        fetchUnits()
      } catch (error) {
        console.error('Error deleting unit:', error)
        const errorMessage = error.response?.data?.detail ||
                            error.response?.data?.message ||
                            'Failed to delete unit. Please try again.'
        
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: errorMessage
        })
      }
    }
  }

  return (
    <>
      <div className="pagetitle">
        <h1>Manage Units</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><a href="/">Home</a></li>
            <li className="breadcrumb-item">Units</li>
            <li className="breadcrumb-item active">Manage Units</li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="row">
          <div className="col-lg-12">
            <div className="card">
              <div className="card-body">
                <h5 className="card-title">
                  <i className="bi bi-rulers me-2"></i>
                  Manage Units
                </h5>

                {/* Filters */}
                <div className="row mb-3">
                  <div className="col-md-6">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search units..."
                      value={filters.search}
                      onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                    />
                  </div>
                </div>

                {/* Units Table */}
                {loading ? (
                  <p>Loading...</p>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-striped">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Abbreviation</th>
                          <th className="text-end" style={{ width: '150px' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredUnits.length === 0 ? (
                          <tr>
                            <td colSpan="3" className="text-center">No units found</td>
                          </tr>
                        ) : (
                          filteredUnits.map(unit => (
                            <tr key={unit.id}>
                              <td className="align-middle">
                                <strong>{unit.name}</strong>
                              </td>
                              <td className="align-middle">
                                {unit.abbreviation ? (
                                  <span className="badge bg-info">{unit.abbreviation}</span>
                                ) : (
                                  <span className="text-muted">-</span>
                                )}
                              </td>
                              <td className="align-middle text-end">
                                <div className="btn-group" role="group">
                                  <button
                                    className="btn btn-sm btn-outline-primary"
                                    onClick={() => handleEdit(unit)}
                                    title="Edit unit"
                                  >
                                    <i className="bi bi-pencil-fill"></i>
                                    <span className="ms-1 d-none d-md-inline">Edit</span>
                                  </button>
                                  <button
                                    className="btn btn-sm btn-outline-danger"
                                    onClick={() => handleDelete(unit.id)}
                                    title="Delete unit"
                                  >
                                    <i className="bi bi-trash-fill"></i>
                                    <span className="ms-1 d-none d-md-inline">Delete</span>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}

export default ManageUnits

